<template>
  <section class="pipeline">
    <router-link
      v-for="stage in stages"
      :key="stage.key"
      :to="stage.to"
      class="stage"
      :class="[stage.tone, { zero: !stage.value }]"
    >
      <span class="stage-value">{{ loaded ? stage.value : '—' }}</span>
      <span class="stage-label">{{ stage.label }}</span>
      <span class="stage-hint">{{ stage.hint }}</span>
    </router-link>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { statsApi } from '../../api'

const REFRESH_MS = 30000

/** 工作台流水线：每一格点进去就是对应的待处理列表。 */
export default {
  name: 'PipelineStrip',
  setup() {
    const data = ref({})
    const loaded = ref(false)
    let timer = null

    const load = async () => {
      try {
        data.value = (await statsApi.getPipelineSummary()) || {}
        loaded.value = true
      } catch {
        /* 流水线统计失败不影响首页其余部分 */
      }
    }

    const stages = computed(() => {
      const d = data.value
      return [
        { key: 'gen', label: '创作中', hint: '生成任务排队 / 运行', value: d.generating || 0, to: '/content/tasks', tone: 'blue' },
        {
          key: 'review',
          label: '待审核',
          hint: '等待审核的文章',
          value: d.review_pending || 0,
          to: { path: '/content/library', query: { review_status: 'pending' } },
          tone: 'warn',
        },
        {
          key: 'ready',
          label: '待发布',
          hint: '已过审，尚未发布',
          value: d.ready_to_publish || 0,
          to: { path: '/content/library', query: { review_status: 'passed', publish_status: 'unpublished' } },
          tone: 'violet',
        },
        {
          key: 'queue',
          label: '发布中',
          hint: '队列排队 / 执行中',
          value: (d.publish_pending || 0) + (d.publish_running || 0),
          to: { path: '/publish', query: { tab: 'jobs', filter: 'pending' } },
          tone: 'blue',
        },
        {
          key: 'done',
          label: '今日已发布',
          hint: '今天发布成功',
          value: d.published_today || 0,
          to: { path: '/publish', query: { tab: 'jobs', filter: 'success' } },
          tone: 'ok',
        },
        {
          key: 'failed',
          label: '发布失败',
          hint: '近 7 天，需要处理',
          value: d.failed_7d || 0,
          to: { path: '/publish', query: { tab: 'jobs', filter: 'failed' } },
          tone: 'bad',
        },
      ]
    })

    onMounted(() => {
      load()
      timer = setInterval(load, REFRESH_MS)
    })
    onBeforeUnmount(() => {
      if (timer) clearInterval(timer)
    })

    return { stages, loaded }
  },
}
</script>

<style scoped>
.pipeline {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.stage {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 14px 12px;
  border-radius: var(--mp-radius-lg);
  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.stage:hover {
  border-color: var(--mp-accent);
  box-shadow: var(--mp-shadow-sm);
}

.stage:not(:last-child)::after {
  content: '›';
  position: absolute;
  right: -9px;
  top: 50%;
  transform: translateY(-50%);
  color: #c4ccda;
  font-size: 16px;
  z-index: 1;
}

.stage-value {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.stage-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-text);
}

.stage-hint {
  font-size: 11px;
  color: var(--mp-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stage.blue .stage-value { color: var(--mp-accent); }
.stage.warn .stage-value { color: #d9822b; }
.stage.violet .stage-value { color: #7c4dda; }
.stage.ok .stage-value { color: var(--mp-success); }
.stage.bad .stage-value { color: #e0573a; }
.stage.zero .stage-value { color: #c4ccda; }

@media (max-width: 1100px) {
  .pipeline {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .stage:nth-child(3)::after {
    display: none;
  }
}

@media (max-width: 768px) {
  .pipeline {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .stage::after {
    display: none;
  }

  .stage-value {
    font-size: 20px;
  }
}
</style>
